import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { QuoteProgress } from '../../canvasComponents/index';
import Final from '../Final/final';
import ServicesLanding from '../../components/Services/ServicesLanding';
import VendorLocator from '../../components/VendorLocator/VendorLocator';
import Uber from '../../components/Uber/Uber';
import Enterprise from '../../components/Enterprise/Enterprise';
import ServicesReview from '../../components/ServicesReview/ServicesReview';
import { getRouteUrl } from '../../FormioComponents/utils';
import { getPolicyAddress } from '../../utils/policy/getPolicyAddress';
import { stepsServices } from '../../utils/progressbar/progressbarSteps';
// Analytics
import * as GA from '../../analytics/googleanalytics';

function Services({ eligibleService, claimId, gwclaimId, audatargetClaimId, claimData, claimStatus, submit, updateClaim, uberAmount }) {
  const policyLocation = JSON.parse(window.sessionStorage.getItem('thePolicy') || null)?.policyLocation?.[0];
  // init GTM
  const sendDataToGTM = useGTMDispatch();
  const [isServicesConfirmed, setIsServicesConfirmed] = useState(!(eligibleService.length > 0));
  const [selectedServiceType, setSelectedServiceType] = useState(claimData?.services?.rentalType);
  const [readyForVendor, setReadyForVendor] = useState(claimData?.services?.lastService === 'audatarget');
  const [showReview, setShowReview] = useState(false);
  const [inReview, setInReview] = useState(false);
  const [isSaveContinueLater, setIsSaveContinueLater] = useState(null);
  const [finalContinue, setFinalContinue] = useState(false);
  const [dataToDB, setDataToDB] = useState(claimData);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageLabel, setCurrentPageLabel] = useState(null);
  const [policyAddress, setPolicyAddress] = useState(policyLocation ? `${policyLocation.addressLine1} ${policyLocation.city} ${policyLocation.provinceCode} ${policyLocation.postalCode}` : null);
  // data for review
  const [uber, setUber] = useState(claimData?.services?.uber);
  const [enterprise, setEnterprise] = useState(claimData?.services?.enterprise);
  const [audatarget, setAudatarget] = useState(claimData?.services?.audatarget);
  const isServicesUpdate = useRef(true);
  const intl = useIntl();

  useEffect(() => {
    const claimType = dataToDB?.claim_type?.claim_type;
    window.history.pushState({}, window.location.pathname, `#/${getRouteUrl(document.querySelector('.services-heading')?.textContent, claimType) || ''}`);

    async function getPolicy() {
      if (window.sessionStorage.getItem('policyDetails') && claimData?.claim_type?.vehicle) {
        setPolicyAddress(getPolicyAddress(claimData.claim_type.vehicle, claimData.lossDate));
      }
    }
    getPolicy();
  }, [dataToDB]);

  const ELIGIBILITY_VENDOR_LOCATOR = 'isAudatargetEligible';
  const ELIGIBILITY_ENTERPRISE = 'isEnterpriseEligible';
  const ELIGIBILITY_UBER = 'isUberEligible';

  const serviceType = (val) => {
    setSelectedServiceType(val);
    setReadyForVendor((val === 'own_rental' || val === 'claim_expert') && showVendorLocator);
    const tempData = { ...dataToDB.services, rentalType: val };
    if (val === 'own_rental') {
      tempData.noService = true;
    }
    if (val !== 'uber' && tempData?.uber) {
      delete tempData.uber;
    } if (val !== 'enterprise' && tempData?.enterprise) {
      delete tempData.enterprise;
    }
    setDataToDB({ ...dataToDB, services: tempData });

    isServicesUpdate.current = false;
    setIsSaveContinueLater(false);
    // send data to google analytics
    sendDataToGTM(GA.sendEventData('updateClaim', { ...dataToDB, services: tempData }, claimStatus));
  };

  const reviewEdit = (service) => {
    const tempData = { ...dataToDB.services, lastService: null, lastStep: null };
    setDataToDB({ ...dataToDB, services: tempData });
    setShowReview(false);
    setInReview(true);
    setReadyForVendor(service === 'audatarget');
    if (service === 'uber' || service === 'enterprise') {
      setUber(null);
      setEnterprise(null);
      setSelectedServiceType('');
      showVendorLocator = false;
    }
  };

  const saveAndContinueLater = (service, step, data, leave, servicesUpdate = true) => {
    isServicesUpdate.current = servicesUpdate;
    const tempData = { ...dataToDB.services, lastStep: step, lastService: service };
    tempData[service] = data;
    setIsSaveContinueLater(leave);
    setDataToDB({ ...dataToDB, services: tempData });
  };

  useEffect(() => {
    if (isSaveContinueLater !== null) {
      updateClaim({ data: dataToDB }, isSaveContinueLater, isServicesUpdate.current);
      sendDataToGTM(GA.sendEventData('updateClaim', dataToDB, claimStatus));
    }
  }, [dataToDB, isSaveContinueLater]);

  useEffect(() => {
    if (!finalContinue) {
      setIsServicesConfirmed(eligibleService.length <= 0);
    }
  }, [eligibleService]);

  const onFinalContinue = (service, data) => {
    setReadyForVendor(true);
    setFinalContinue(true);
    setShowReview(service === 'audatarget' || !showVendorLocator);
    if (service === 'uber') {
      setUber(data);
    } else if (service === 'enterprise') {
      setEnterprise(data);
    } else if (service === 'audatarget') {
      setAudatarget(data);
    }
  };

  const showUber = eligibleService.length > 0 && eligibleService.includes(ELIGIBILITY_UBER) && selectedServiceType === 'uber';
  const showEnterprise = eligibleService.length > 0 && eligibleService.includes(ELIGIBILITY_ENTERPRISE) && selectedServiceType === 'enterprise';
  const showServicesLandingPage = !selectedServiceType && (eligibleService.includes(ELIGIBILITY_UBER) || eligibleService.includes(ELIGIBILITY_ENTERPRISE));
  const showRental = eligibleService.length > 0 && selectedServiceType === 'own_rental' && !eligibleService.includes(ELIGIBILITY_VENDOR_LOCATOR);
  let showVendorLocator = eligibleService.length > 0 && eligibleService.includes(ELIGIBILITY_VENDOR_LOCATOR);
  const showClaimExpert = eligibleService.length > 0 && selectedServiceType === 'claim_expert' && !(showVendorLocator && readyForVendor && !showReview);

  if (!eligibleService.includes(ELIGIBILITY_UBER) && !eligibleService.includes(ELIGIBILITY_ENTERPRISE) && !inReview && !readyForVendor) {
    setReadyForVendor(true);
  }

  return (
    isServicesConfirmed === false ? (
      eligibleService.length >= 1 && (
        <>
          {currentPage && currentPageLabel && (
            <QuoteProgress
              id="progressBar"
              title={intl.formatMessage({ id: 'Services', defaultValue: 'Services' })}
              stepTitle={intl.formatMessage(currentPageLabel)}
              progress={stepsServices[currentPage - 1] && stepsServices[currentPage - 1].progress}
              totalSteps={11}
              primary
              mbXl
            />
          )}
          {showServicesLandingPage && !readyForVendor && !showReview && (
            <ServicesLanding
              serviceTypeCallback={(val) => serviceType(val)}
              claimData={dataToDB}
              serviceData={dataToDB.services}
              uber={eligibleService.includes(ELIGIBILITY_UBER)}
              uberAmount={uberAmount}
              enterprise={eligibleService.includes(ELIGIBILITY_ENTERPRISE)}
              saveAndContinueLater={(service, step, data, leave) => saveAndContinueLater(service, step, data, leave)}
              updateCurrentPage={(pageLabel, pageNumber) => { setCurrentPageLabel(pageLabel); setCurrentPage(pageNumber); }}
            />
          )}
          {showUber && !readyForVendor && !showReview && (
            <Uber
              onFinalContinue={(data) => onFinalContinue('uber', data)}
              saveAndContinueLater={(service, step, data) => saveAndContinueLater(service, step, data, true)}
              updateClaim={(service, step, data) => saveAndContinueLater(service, step, data, false)}
              claimId={claimId}
              serviceData={dataToDB.services?.uber}
              lastStep={dataToDB?.services?.lastService === 'uber' && dataToDB?.services?.lastStep}
              updateCurrentPage={(pageLabel, pageNumber) => { setCurrentPageLabel(pageLabel); setCurrentPage(pageNumber); }}
              uberAmount={uberAmount}
            />
          )}
          {showEnterprise && !readyForVendor && !showReview && (
            <Enterprise
              onFinalContinue={(data) => onFinalContinue('enterprise', data)}
              saveAndContinueLater={(service, step, data) => saveAndContinueLater(service, step, data, true)}
              updateClaim={(service, step, data) => saveAndContinueLater(service, step, data, false)}
              policyEmail={claimData?.container?.policyHolder?.email?.value}
              gwclaimId={gwclaimId}
              serviceData={dataToDB.services?.enterprise}
              policyAddress={policyAddress}
              lastStep={dataToDB?.services?.lastService === 'enterprise' && dataToDB?.services?.lastStep}
              updateCurrentPage={(pageLabel, pageNumber) => { setCurrentPageLabel(pageLabel); setCurrentPage(pageNumber); }}
            />
          )}
          {showVendorLocator && readyForVendor && !showReview && (
            <VendorLocator
              onFinalContinue={(data) => onFinalContinue('audatarget', data)}
              saveAndContinueLater={(service, step, data) => saveAndContinueLater(service, step, data, true)}
              updateClaim={(service, step, data) => saveAndContinueLater(service, step, data, false)}
              audatargetClaimId={audatargetClaimId}
              gwclaimId={gwclaimId}
              serviceData={dataToDB.services?.audatarget}
              policyAddress={policyAddress}
              vehicle={claimData.claim_type?.vehicle}
              lastStep={dataToDB?.services?.lastService === 'audatarget' && dataToDB?.services?.lastStep}
              updateCurrentPage={(pageLabel, pageNumber) => { setCurrentPageLabel(pageLabel); setCurrentPage(pageNumber); }}
            />
          )}
          {(showRental || showClaimExpert || showReview) && (
            <ServicesReview
              claimDetails={claimData}
              onFinalContinue={() => { submit(gwclaimId); setFinalContinue(true); setIsServicesConfirmed(true); }}
              reviewEdit={(service) => reviewEdit(service)}
              noService={selectedServiceType}
              uber={uber}
              enterprise={enterprise}
              audatarget={audatarget}
              updateCurrentPage={(pageLabel, pageNumber) => { setCurrentPageLabel(pageLabel); setCurrentPage(pageNumber); }}
            />
          )}
        </>
      )
    ) : (
      ((claimData?.lossDetailsConfirmed && eligibleService.length <= 0) || (finalContinue && (showReview || showRental || showClaimExpert)))
        && <Final claimId={claimId} />
    )
  );
}

export default Services;
